/**
 * PdfFieldEditor
 *
 * Renders a PDF page and lets the merchant drag-place and resize signature /
 * text / date / checkbox fields on top of it. Field positions are stored as
 * percentages of the page so they're resolution-independent and match what the
 * NestJS backend writes into the signed PDF via pdf-lib.
 *
 * Layout:
 *   ┌──────────────┬────────────────────────────┬──────────────────┐
 *   │  Add-field   │   PDF + overlay             │  Selected field  │
 *   │  type picker │   (drag, resize)            │  properties      │
 *   └──────────────┴────────────────────────────┴──────────────────┘
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Document, Page } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import {
  Badge,
  BlockStack,
  Button,
  Checkbox,
  Divider,
  InlineStack,
  Select,
  Spinner,
  Text,
  TextField,
  Tooltip,
} from '@shopify/polaris';
import { useApi } from '../hooks/useApi';
import { signerColor } from '../multiSigner';
import type { FieldDraft, FieldType, SignerOption, TemplateField } from '../types';

// ─── colour per field type ────────────────────────────────────────────────────

const TYPE_COLOR: Record<FieldType, string> = {
  SIGNATURE: '#7C3AED',
  INITIALS:  '#6D28D9',
  TEXT:      '#2563EB',
  NAME:      '#1D4ED8',
  EMAIL:     '#0369A1',
  DATE:      '#0891B2',
  CHECKBOX:  '#059669',
};

const FIELD_TYPES: { type: FieldType; label: string; defaultW: number; defaultH: number }[] = [
  { type: 'SIGNATURE', label: 'Signature',  defaultW: 30, defaultH: 8  },
  { type: 'INITIALS',  label: 'Initials',   defaultW: 12, defaultH: 8  },
  { type: 'TEXT',      label: 'Text',       defaultW: 28, defaultH: 6  },
  { type: 'NAME',      label: 'Name',       defaultW: 28, defaultH: 6  },
  { type: 'EMAIL',     label: 'Email',      defaultW: 28, defaultH: 6  },
  { type: 'DATE',      label: 'Date',       defaultW: 18, defaultH: 6  },
  { type: 'CHECKBOX',  label: 'Checkbox',   defaultW: 6,  defaultH: 6  },
];

// ─── helpers ─────────────────────────────────────────────────────────────────

function localId() {
  return `_${Math.random().toString(36).slice(2)}`;
}

function fromServerField(f: TemplateField): FieldDraft {
  return {
    _localId: f.id,
    serverId: f.id,
    type: f.type,
    label: f.label,
    required: f.required,
    pageNumber: f.pageNumber,
    posX: f.posX,
    posY: f.posY,
    width: f.width,
    height: f.height,
    placeholder: f.placeholder ?? '',
    sortOrder: f.sortOrder,
    signerPosition: f.signerPosition ?? 1,
  };
}

// ─── props ────────────────────────────────────────────────────────────────────

interface Props {
  templateId: string;
  pdfUrl: string;
  pageCount: number;
  initialFields: TemplateField[];
  onSaved: (fields: TemplateField[]) => void;
  /** Multi-signer: who can fill fields (customer first). One entry = classic one-signer editor. */
  signers?: SignerOption[];
  /** False for written templates, whose signers are chosen in the text editor. */
  signerEditable?: boolean;
  /**
   * Written templates: fields come from the text and are rebuilt on every editor
   * save, so this screen only previews them (no adding, moving or saving here).
   */
  readOnly?: boolean;
}

// ─── FieldBox ─────────────────────────────────────────────────────────────────

interface FieldBoxProps {
  field: FieldDraft;
  selected: boolean;
  onSelect: () => void;
  onDragStart: (e: React.PointerEvent, id: string) => void;
  onResizeStart: (e: React.PointerEvent, id: string) => void;
  /** Multi-signer: colour by signer and show their number. */
  multiSigner: boolean;
}

function FieldBox({ field, selected, onSelect, onDragStart, onResizeStart, multiSigner }: FieldBoxProps) {
  const color = multiSigner ? signerColor(field.signerPosition) : TYPE_COLOR[field.type];
  return (
    <div
      style={{
        position:  'absolute',
        left:      `${field.posX}%`,
        top:       `${field.posY}%`,
        width:     `${field.width}%`,
        height:    `${field.height}%`,
        border:    `2px solid ${color}`,
        background: selected ? `${color}22` : `${color}11`,
        cursor:    'grab',
        boxSizing: 'border-box',
        display:   'flex',
        alignItems: 'center',
        justifyContent: 'center',
        userSelect: 'none',
        outline:   selected ? `2px solid ${color}` : 'none',
        outlineOffset: '1px',
        zIndex:    selected ? 20 : 10,
      }}
      onPointerDown={(e) => {
        e.stopPropagation();
        onSelect();
        onDragStart(e, field._localId);
      }}
    >
      <span style={{
        fontSize: '10px', color, fontWeight: 600, overflow: 'hidden', pointerEvents: 'none',
        // The page wrapper sets line-height 0, which hides labels; multi-signer needs the signer number visible.
        ...(multiSigner ? { lineHeight: 'normal', whiteSpace: 'nowrap', textOverflow: 'ellipsis', padding: '0 4px' } : {}),
      }}>
        {multiSigner ? `${field.signerPosition} · ${field.label}` : field.label}
      </span>
      {/* Resize handle — bottom-right corner */}
      {selected && (
        <div
          style={{
            position:   'absolute',
            bottom:     -1,
            right:      -1,
            width:      10,
            height:     10,
            background: color,
            cursor:     'se-resize',
          }}
          onPointerDown={(e) => {
            e.stopPropagation();
            onResizeStart(e, field._localId);
          }}
        />
      )}
    </div>
  );
}

// ─── Main editor ─────────────────────────────────────────────────────────────

export default function PdfFieldEditor({
  templateId,
  pdfUrl,
  pageCount,
  initialFields,
  onSaved,
  signers = [{ position: 1, label: 'Customer' }],
  signerEditable = true,
  readOnly = false,
}: Props) {
  const { request } = useApi();
  const multiSigner = signers.length > 1;
  // New fields are added for this signer.
  const [addFor, setAddFor] = useState(1);

  const [fields, setFields] = useState<FieldDraft[]>(
    initialFields.map(fromServerField),
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [numPages, setNumPages] = useState(pageCount);
  const [pdfWidth, setPdfWidth] = useState(640);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const overlayRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Observe container width to keep PDF responsive.
  useEffect(() => {
    if (!containerRef.current) return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width;
      if (w && w > 0) setPdfWidth(Math.min(w - 4, 800));
    });
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  const pageFields = fields.filter((f) => f.pageNumber === currentPage);
  const selected = fields.find((f) => f._localId === selectedId) ?? null;

  // ---- drag state ----
  const dragRef = useRef<{
    fieldId: string;
    startX: number; startY: number;
    origPosX: number; origPosY: number;
  } | null>(null);

  const resizeRef = useRef<{
    fieldId: string;
    startX: number; startY: number;
    origW: number; origH: number;
  } | null>(null);

  const pxToPercX = (px: number) => {
    const w = overlayRef.current?.getBoundingClientRect().width ?? 1;
    return (px / w) * 100;
  };
  const pxToPercY = (px: number) => {
    const h = overlayRef.current?.getBoundingClientRect().height ?? 1;
    return (px / h) * 100;
  };

  const handleDragStart = useCallback((e: React.PointerEvent, id: string) => {
    if (readOnly) return;
    const field = fields.find((f) => f._localId === id);
    if (!field) return;
    (e.target as Element).setPointerCapture(e.pointerId);
    dragRef.current = {
      fieldId: id,
      startX: e.clientX,
      startY: e.clientY,
      origPosX: field.posX,
      origPosY: field.posY,
    };
  }, [fields, readOnly]);

  const handleResizeStart = useCallback((e: React.PointerEvent, id: string) => {
    if (readOnly) return;
    const field = fields.find((f) => f._localId === id);
    if (!field) return;
    (e.target as Element).setPointerCapture(e.pointerId);
    resizeRef.current = {
      fieldId: id,
      startX: e.clientX,
      startY: e.clientY,
      origW: field.width,
      origH: field.height,
    };
  }, [fields, readOnly]);

  const handleOverlayMove = useCallback((e: React.PointerEvent) => {
    if (dragRef.current) {
      const { fieldId, startX, startY, origPosX, origPosY } = dragRef.current;
      const field = fields.find((f) => f._localId === fieldId);
      if (!field) return;
      const dx = pxToPercX(e.clientX - startX);
      const dy = pxToPercY(e.clientY - startY);
      const newX = Math.max(0, Math.min(100 - field.width, origPosX + dx));
      const newY = Math.max(0, Math.min(100 - field.height, origPosY + dy));
      setFields((prev) =>
        prev.map((f) => f._localId === fieldId ? { ...f, posX: newX, posY: newY } : f),
      );
    } else if (resizeRef.current) {
      const { fieldId, startX, startY, origW, origH } = resizeRef.current;
      const field = fields.find((f) => f._localId === fieldId);
      if (!field) return;
      const dw = pxToPercX(e.clientX - startX);
      const dh = pxToPercY(e.clientY - startY);
      const newW = Math.max(4, Math.min(100 - field.posX, origW + dw));
      const newH = Math.max(2, Math.min(100 - field.posY, origH + dh));
      setFields((prev) =>
        prev.map((f) => f._localId === fieldId ? { ...f, width: newW, height: newH } : f),
      );
    }
  }, [fields]);

  const handleOverlayUp = useCallback(() => {
    dragRef.current = null;
    resizeRef.current = null;
  }, []);

  // ---- add field ----
  const addField = useCallback((type: FieldType) => {
    const def = FIELD_TYPES.find((t) => t.type === type)!;
    const draft: FieldDraft = {
      _localId: localId(),
      serverId: null,
      type,
      label: def.label,
      required: true,
      pageNumber: currentPage,
      posX: 10,
      posY: 10,
      width: def.defaultW,
      height: def.defaultH,
      placeholder: '',
      sortOrder: fields.length,
      signerPosition: multiSigner ? addFor : 1,
    };
    setFields((prev) => [...prev, draft]);
    setSelectedId(draft._localId);
  }, [fields.length, currentPage, multiSigner, addFor]);

  // ---- remove field ----
  const removeField = useCallback((id: string) => {
    setFields((prev) => prev.filter((f) => f._localId !== id));
    if (selectedId === id) setSelectedId(null);
  }, [selectedId]);

  // ---- update selected field property ----
  const updateSelected = useCallback(
    (patch: Partial<FieldDraft>) => {
      if (!selectedId) return;
      setFields((prev) =>
        prev.map((f) => (f._localId === selectedId ? { ...f, ...patch } : f)),
      );
    },
    [selectedId],
  );

  // ---- save to server ----
  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      const saved = await request<TemplateField[]>(
        `/templates/${templateId}/fields`,
        {
          method: 'PUT',
          body: JSON.stringify({
            fields: fields.map((f, i) => ({
              type: f.type,
              label: f.label,
              required: f.required,
              pageNumber: f.pageNumber,
              posX: Math.round(f.posX * 100) / 100,
              posY: Math.round(f.posY * 100) / 100,
              width: Math.round(f.width * 100) / 100,
              height: Math.round(f.height * 100) / 100,
              placeholder: f.placeholder || undefined,
              sortOrder: i,
              // Only sent for multi-signer templates (one-signer requests stay as before).
              ...(multiSigner ? { signerPosition: f.signerPosition } : {}),
            })),
          }),
        },
      );
      // Reconcile server IDs back into local state.
      setFields(saved.map(fromServerField));
      onSaved(saved);
    } catch (e: any) {
      setSaveError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ display: 'flex', minHeight: 600, borderTop: '1px solid var(--p-color-border)' }}>
      {/* ── Left: field type palette (hidden when previewing a written template) ── */}
      {!readOnly && <div
        style={{
          width: 140,
          padding: '16px 12px',
          borderRight: '1px solid var(--p-color-border)',
          flexShrink: 0,
        }}
      >
        <BlockStack gap="200">
          {multiSigner && signerEditable && (
            <Select
              label="Add fields for"
              options={signers.map((s) => ({ label: `${s.position}. ${s.label}`, value: String(s.position) }))}
              value={String(addFor)}
              onChange={(v) => setAddFor(Number(v))}
            />
          )}
          <Text variant="headingSm" as="h3">Add field</Text>
          {FIELD_TYPES.map(({ type, label }) => (
            <button
              key={type}
              onClick={() => addField(type)}
              style={{
                width: '100%',
                textAlign: 'left',
                padding: '6px 8px',
                border: `1px solid ${TYPE_COLOR[type]}`,
                borderRadius: 4,
                background: `${TYPE_COLOR[type]}11`,
                color: TYPE_COLOR[type],
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              {label}
            </button>
          ))}
        </BlockStack>
      </div>}

      {/* ── Centre: PDF + overlay ──────────────────────────────────────── */}
      <div
        ref={containerRef}
        style={{
          flex: 1,
          padding: 16,
          overflow: 'auto',
          background: '#f6f6f7',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 12,
        }}
      >
        {/* Page navigation */}
        <InlineStack gap="200" blockAlign="center">
          <Button
            size="slim"
            disabled={currentPage <= 1}
            onClick={() => setCurrentPage((p) => p - 1)}
          >
            ‹
          </Button>
          <Text as="span" variant="bodySm">
            Page {currentPage} of {numPages}
          </Text>
          <Button
            size="slim"
            disabled={currentPage >= numPages}
            onClick={() => setCurrentPage((p) => p + 1)}
          >
            ›
          </Button>
        </InlineStack>

        {/* PDF page + field overlay */}
        <div
          style={{
            position: 'relative',
            display: 'inline-block',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            lineHeight: 0,
          }}
        >
          <Document file={pdfUrl} loading={<Spinner />} onLoadSuccess={({ numPages: n }) => setNumPages(n)}>
            <Page
              pageNumber={currentPage}
              width={pdfWidth}
              renderTextLayer={false}
              renderAnnotationLayer={false}
            />
          </Document>

          {/* Transparent overlay that captures all pointer events */}
          <div
            ref={overlayRef}
            style={{ position: 'absolute', inset: 0 }}
            onPointerMove={handleOverlayMove}
            onPointerUp={handleOverlayUp}
            onPointerDown={() => setSelectedId(null)} // click empty → deselect
          >
            {pageFields.map((field) => (
              <FieldBox
                key={field._localId}
                field={field}
                selected={field._localId === selectedId}
                onSelect={() => setSelectedId(field._localId)}
                onDragStart={handleDragStart}
                onResizeStart={handleResizeStart}
                multiSigner={multiSigner}
              />
            ))}
          </div>
        </div>

        {/* Save row */}
        {!readOnly && <InlineStack gap="300" blockAlign="center">
          {saveError && (
            <Text tone="critical" as="span" variant="bodySm">{saveError}</Text>
          )}
          <Button variant="primary" loading={saving} onClick={handleSave}>
            Save layout
          </Button>
        </InlineStack>}
      </div>

      {/* ── Right: selected field properties ──────────────────────────── */}
      <div
        style={{
          width: 220,
          padding: '16px 12px',
          borderLeft: '1px solid var(--p-color-border)',
          flexShrink: 0,
          overflowY: 'auto',
        }}
      >
        {readOnly ? (
          // Written template: information only — fields are edited in the text editor.
          selected ? (
            <BlockStack gap="200">
              <InlineStack align="space-between" blockAlign="center">
                <Text variant="headingSm" as="h3">Field</Text>
                <Badge>{selected.type}</Badge>
              </InlineStack>
              <Text as="p" fontWeight="semibold">{selected.label}</Text>
              {multiSigner && (
                <Text as="p" variant="bodySm">
                  Filled by {signers.find((s) => s.position === selected.signerPosition)?.label ?? 'Customer'}
                </Text>
              )}
              <Text as="p" variant="bodySm" tone="subdued">{selected.required ? 'Required' : 'Optional'} · page {selected.pageNumber}</Text>
              <Divider />
              <Text as="p" variant="bodySm" tone="subdued">To change this field, open the text editor.</Text>
            </BlockStack>
          ) : (
            <BlockStack gap="200">
              <Text variant="headingSm" as="h3">Preview</Text>
              <Text variant="bodySm" tone="subdued" as="p">
                These are the fields from your written template. Click one to see its details.
              </Text>
            </BlockStack>
          )
        ) : selected ? (
          <BlockStack gap="300">
            <InlineStack align="space-between" blockAlign="center">
              <Text variant="headingSm" as="h3">Field</Text>
              <Badge>{selected.type}</Badge>
            </InlineStack>

            {multiSigner && (
              <Select
                label="Filled by"
                options={signers.map((s) => ({ label: `${s.position}. ${s.label}`, value: String(s.position) }))}
                value={String(selected.signerPosition)}
                onChange={(v) => updateSelected({ signerPosition: Number(v) })}
                disabled={!signerEditable}
                helpText={signerEditable ? undefined : 'Change who fills this field in the text editor.'}
              />
            )}

            <TextField
              label="Label"
              value={selected.label}
              onChange={(v) => updateSelected({ label: v })}
              autoComplete="off"
            />

            <Select
              label="Type"
              options={FIELD_TYPES.map((t) => ({ label: t.label, value: t.type }))}
              value={selected.type}
              onChange={(v) => updateSelected({ type: v as FieldType })}
            />

            {(selected.type === 'TEXT' || selected.type === 'NAME' || selected.type === 'EMAIL') && (
              <TextField
                label="Placeholder"
                value={selected.placeholder}
                onChange={(v) => updateSelected({ placeholder: v })}
                autoComplete="off"
              />
            )}

            <Checkbox
              label="Required"
              checked={selected.required}
              onChange={(v) => updateSelected({ required: v })}
            />

            <Divider />

            <Text variant="bodySm" tone="subdued" as="p">
              Page {selected.pageNumber} · ({selected.posX.toFixed(1)}%, {selected.posY.toFixed(1)}%)
              · {selected.width.toFixed(1)} × {selected.height.toFixed(1)}%
            </Text>

            <Button
              tone="critical"
              size="slim"
              onClick={() => removeField(selected._localId)}
            >
              Remove field
            </Button>
          </BlockStack>
        ) : (
          <BlockStack gap="200">
            <Text variant="headingSm" as="h3">Properties</Text>
            <Text variant="bodySm" tone="subdued" as="p">
              Select a field to edit its properties, or use the palette on the
              left to add a new field to this page.
            </Text>
          </BlockStack>
        )}
      </div>
    </div>
  );
}
